import type { Components, Theme } from '@mui/material/styles';

import { radii } from './radii';
import { accentShadows } from './shadows';

/**
 * MUI component overrides — keep flat radius (12-16), no default elevation
 * on Buttons, colored shadow on primary CTAs.
 *
 * Note: typed as `Components<Theme>` so theme-aware callbacks (palette access)
 * stay type-safe when the consuming app extends the theme.
 */
export const components: Components<Omit<Theme, 'components'>> = {
  MuiCssBaseline: {
    styleOverrides: {
      ':root': {
        // Plus Jakarta Sans + Inter look noticeably better with these OpenType
        // features enabled (alt 'a', double-storey 'g', ss03 punctuation).
        fontFeatureSettings: '"cv11", "ss01", "ss03"',
      },
      // Prevent horizontal scroll caused by decorative absolutely-positioned
      // elements (organic blobs, phone mockup) that intentionally extend
      // beyond their section's `overflow: hidden` for layered visuals.
      // Must be on `html` AND `body` — Safari mobile honours `html`, others honour `body`.
      html: {
        overflowX: 'hidden',
      },
      body: {
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
        overflowX: 'hidden',
      },
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
      disableRipple: false,
    },
    styleOverrides: {
      root: {
        borderRadius: radii.md,
        paddingInline: 20,
        paddingBlock: 10,
        fontWeight: 600,
        transition: 'transform 150ms cubic-bezier(0.2, 0, 0, 1), box-shadow 150ms cubic-bezier(0.2, 0, 0, 1), background-color 150ms cubic-bezier(0.2, 0, 0, 1)',
        '&:active': {
          transform: 'translateY(1px)',
        },
      },
      containedPrimary: {
        boxShadow: accentShadows.primary,
        '&:hover': {
          boxShadow: accentShadows.primaryHover,
        },
      },
      containedWarning: {
        boxShadow: accentShadows.accent,
        '&:hover': {
          boxShadow: accentShadows.accentHover,
        },
      },
      sizeLarge: {
        paddingInline: 28,
        paddingBlock: 14,
        fontSize: '1rem',
      },
      sizeSmall: {
        paddingInline: 14,
        paddingBlock: 6,
        fontSize: '0.8125rem',
      },
    },
  },

  MuiCard: {
    defaultProps: {
      elevation: 2,
    },
    styleOverrides: {
      root: {
        borderRadius: radii.lg,
        backgroundImage: 'none',
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: radii.lg,
        backgroundImage: 'none',
      },
      rounded: {
        borderRadius: radii.lg,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: radii.full,
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: radii.md,
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: radii.md,
      },
    },
  },

  MuiAppBar: {
    defaultProps: {
      elevation: 0,
      color: 'transparent',
    },
    styleOverrides: {
      root: {
        backdropFilter: 'saturate(180%) blur(12px)',
      },
    },
  },

  MuiLink: {
    defaultProps: {
      underline: 'hover',
    },
    styleOverrides: {
      root: {
        fontWeight: 500,
      },
    },
  },
};
